import React, {FC, useState, useEffect, memo, useRef} from "react";
import {
    getLabelName,
    isArabicCurrentLanguage,
    formatValue
} from "../../../utils";
import { Form } from "react-bootstrap";
import {CommonTextBoxProps} from "./commonTextBoxProps";

type TextBoxV2Props = CommonTextBoxProps & {
    optimizeTyping?: boolean;
    resetKey?: string;
    // min?: number | string;
    // step?: number | string;
};

export const TextBoxV2: FC<TextBoxV2Props> = ({
                                                  labelName,
                                                  inputName,
                                                  inputValue,
                                                  errorText,
                                                  placeHolder,
                                                  isMandatory = false,
                                                  isReadOnly = false,
                                                  isVisible = true,
                                                  type = "text",
                                                  onChange,
                                                  onBlur,
                                                  onKeyPress,
                                                  controlSize = "col flex-form-control",
                                                  labelSize = "form-label",
                                                  inputRef,
                                                  formatType = "none",
                                                  preventNegative = false,
                                                  optimizeTyping = false,
                                                  resetKey = '',
                                                  trackValueChangeOnBlur = true,
                                                  inputContainerClassName,className,
                                                  autoComplete="off",
                                                  row = 3,
                                                  style
                                              }) => {
    if (!isVisible) return null;
    // Only use optimization for text types (not checkboxes, etc.)
    const shouldOptimize = optimizeTyping &&
        (type === "text" || type === "textarea" || type === "password" || type === "quantity" || type === "price" || type === "number");
    // Local state for input value
    const [localValue, setLocalValue] = useState(
        type === "checkbox" ? Boolean(inputValue) : formatValue(inputValue, formatType)
    );
    const originalValueRef = useRef<string>("");
    const asString = () =>type === "checkbox" ? String(Boolean(localValue)) : String(localValue ?? "");
    //#region useEffect
    // Update the local state when input value or resetKey changes
    useEffect(() => {
        if (type === "checkbox") {
            setLocalValue(Boolean(inputValue));
        } else {
            setLocalValue(formatValue(inputValue, formatType));
        }
    }, [inputValue, type, formatType, resetKey]);
    useEffect(() => {
        if (type === "checkbox") {
            setLocalValue(Boolean(inputValue));
        } else {
            const formatted = formatValue(inputValue, formatType);
            setLocalValue(formatted);
            originalValueRef.current = String(formatted); // keep baseline in sync
        }
    }, [inputValue, type, formatType, resetKey]);
    //#endregion
    //#region variables
    const cssPrefix: string = isArabicCurrentLanguage() ? "_ar" : "_en";
    const errorClassName =
        errorText !== null && errorText !== undefined
            ? `error${cssPrefix}`
            : `success${cssPrefix}`;
    const inputClassName: string =className !=null && className !=""?className:
        type === "checkbox" ? "" : `form-control ${errorClassName}`;
    const isInputField: boolean = type !== "textarea";
    const translatedPlaceholder = getLabelName(placeHolder || labelName);
    //#endregion
    //#region function
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        // For checkbox, always pass through to onChange
        if (type === "checkbox") {
            if (onChange) {
                onChange(e);
            }
            return;
        }

        // For optimized text fields, update local state only
        if (shouldOptimize) {
            setLocalValue(e.target.value);
        }
        // For non-optimized fields, pass through to original handler
        else if (onChange) {
            onChange(e);
        }
    };
    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        // For optimized fields, update parent state on blur
        if (shouldOptimize && onChange) {
            const prevVal = originalValueRef.current;
            const nextVal = asString();
            const changed = prevVal !== nextVal;
            if (trackValueChangeOnBlur || changed) {
                const syntheticEvent = {
                    target: { name: inputName, value: localValue },
                };
                onChange(syntheticEvent as any);
            }
        }
        // Call the original onBlur handler
        onBlur?.(e);
    };
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (preventNegative && (type === "number" || type === "quantity" || type === "price")) {
            if (event.key === "-" || event.key === "e") {
                event.preventDefault();
                return;
            }
        }

        if (event.key === "Enter") {
            event.preventDefault();

            // For optimized fields, update parent state on Enter key
            if (shouldOptimize && onChange) {
                const syntheticEvent = {
                    target: {
                        name: inputName,
                        value: localValue
                    }
                };
                onChange(syntheticEvent);
            }

            if (onKeyPress) {
                const value = typeof localValue === 'string'
                    ? localValue
                    : String(localValue);
                onKeyPress(value);
            }
        }
    };
    const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        originalValueRef.current = asString();};
    const renderError = () => {
        if (errorText) {
            return (
                <Form.Control.Feedback
                    type="invalid"
                    className="position-relative d-block"
                >
                    {errorText}
                </Form.Control.Feedback>
            );
        }
        return null;
    };
    //#endregion
    //#region html
    return (
        <>
            <div className={controlSize} style={{ gap: 15, alignItems: "baseline" }}>
                {labelName && (
                    <label htmlFor={inputName} className={labelSize}>
                        {getLabelName(labelName)}
                        {isMandatory && <span className="asterisk">*</span>}
                    </label>
                )}
                <div className={ inputContainerClassName ? inputContainerClassName : "flex-grow-1"}>
                    {isInputField ? (
                        <input
                            id={inputName}
                            //key={`${inputName}-${resetKey}`} // Key to force re-render
                            name={inputName}
                            className={inputClassName}
                            type={type === "checkbox" ? "checkbox" : (type || "text")}
                            placeholder={translatedPlaceholder}
                            readOnly={isReadOnly}
                            checked={type === "checkbox" ? Boolean(localValue) : undefined}
                            value={type !== "checkbox" ? localValue : undefined}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                            onKeyDown={handleKeyDown}
                            onWheel={(event) => {
                                event.currentTarget.blur();
                            }}
                            ref={inputRef}
                            min={
                                (type === "number" || type === "quantity" || type === "price") && preventNegative
                                    ? "0"
                                    : undefined
                            }
                            step={
                                type === "price"
                                    ? "0.01"
                                    : type === "quantity"
                                        ? "1"
                                        : type === "number"
                                            ? "0.00001"
                                            : undefined
                            }
                            style={style}
                            autoComplete={autoComplete}
                        />
                    ) : (
                        <textarea
                            id={inputName}
                            key={`${inputName}-${resetKey}`} // Key to force re-render
                            name={inputName}
                            className={inputClassName}
                            rows={row}
                            placeholder={translatedPlaceholder}
                            readOnly={isReadOnly}
                            value={localValue}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            style={{ width: "100%" }}
                        />
                    )}
                    {renderError()}
                </div>
            </div>
        </>
    );
    //#endregion
};

export default memo(TextBoxV2);